<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <div class="logo">
      <router-link :to="localPath('/')" class="home-link">
        <img src="@/assets/logo.png" alt="Koke Logo" class="logo-img" />
        <span>KokeManga</span>
      </router-link>
    </div>
    <div class="nav-right">
      <div class="nav-menu">
        <router-link :to="localPath('/')" class="nav-item">{{ $t('manga.navbar.home') }}</router-link>
        <div class="nav-item" :class="{ active: activeTab === 'free' }" @click="setActiveTab('free')">{{ $t('manga.navbar.freeQueue') }}</div>
        <div class="nav-item" :class="{ active: activeTab === 'vip' }" @click="setActiveTab('vip')">{{ $t('manga.navbar.vipQueue') }}</div>
      </div>
      <div class="lang-dropdown" @mouseenter="showLangMenu = true" @mouseleave="showLangMenu = false">
        <div class="lang-dropdown-trigger">
          <i class="fas fa-globe"></i>
          <span>{{ langLabel }}</span>
          <i class="fas fa-chevron-down" :class="{ open: showLangMenu }"></i>
        </div>
        <transition name="fade">
          <div v-if="showLangMenu" class="lang-dropdown-menu">
            <div v-for="lang in langOptions" :key="lang.value" class="lang-dropdown-item"
              :class="{ active: locale === lang.value }" @click="selectLang(lang.value)">
              <span>{{ lang.label }}</span>
              <i v-if="locale === lang.value" class="fas fa-check"></i>
            </div>
          </div>
        </transition>
      </div>
      <div class="user-avatar" @click="handleAvatarClick">
        <div @mouseenter="handleAvatarMouseEnter" @mouseleave="handleAvatarMouseLeave">
          <img src="@/assets/logo.png" :alt="$t('manga.navbar.user')" class="avatar-img" />
        </div>
        <div v-if="isLoggedIn && showUserMenu" class="user-dropdown" @mouseenter="handleDropdownMouseEnter"
          @mouseleave="handleDropdownMouseLeave">
          <div class="user-profile-section">
            <div class="user-avatar-large">{{ getUserInitials() }}</div>
            <div class="user-info-dropdown">
              <div class="user-name-dropdown">{{ getUserDisplayName() }}</div>
              <div class="user-login-method">
                <i class="fas fa-user-circle"></i>
                <span>{{ $t('manga.navbar.loggedIn') }}</span>
              </div>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <div class="resource-section">
            <div class="resource-item">
              <div class="resource-icon" style="color: #9c27b0;">
                <i class="fas fa-tasks"></i>
              </div>
              <div class="resource-info">
                <div class="resource-title">{{ $t('manga.navbar.currentTasks') }}</div>
                <div class="resource-value">{{ getTaskCount() }}</div>
              </div>
            </div>
            <div class="resource-item">
              <div class="resource-icon" style="color: #2196f3;">
                <i class="fas fa-bolt"></i>
              </div>
              <div class="resource-info">
                <div class="resource-title">{{ $t('manga.navbar.currentCredits') }}</div>
                <div class="resource-value">{{ getComputingPower() }}</div>
              </div>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleGetPower">
            <div class="dropdown-icon" style="color: #ff9500;">
              <i class="fas fa-battery-full"></i>
            </div>
            <span>{{ $t('manga.navbar.getCredits') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>

          <!-- <div class="dropdown-divider"></div>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleProfile">
            <div class="dropdown-icon" style="color: #ff6b6b;">
              <i class="fas fa-gift"></i>
            </div>
            <span>分享有礼</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a> -->

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleMyOrders">
            <div class="dropdown-icon" style="color: #4ecdc4;">
              <i class="fas fa-file-alt"></i>
            </div>
            <span>{{ $t('manga.navbar.myOrders') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleMyTranslations">
            <div class="dropdown-icon" style="color: #1e90ff;">
              <i class="fas fa-language"></i>
            </div>
            <span>{{ $t('manga.navbar.myTranslations') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleUserProfile">
            <div class="dropdown-icon" style="color: #1a936f;">
              <i class="fas fa-user"></i>
            </div>
            <span>{{ $t('manga.navbar.profile') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleUserMessage">
            <div class="dropdown-icon" style="color: #f59e0b;">
              <i class="fas fa-comment-dots"></i>
            </div>
            <span>{{ $t('manga.navbar.feedback') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>

          <div class="dropdown-divider"></div>

          <a href="#" class="dropdown-item-with-icon" @click.prevent="handleLogout">
            <div class="dropdown-icon" style="color: #9d4edd;">
              <i class="fas fa-sign-out-alt"></i>
            </div>
            <span>{{ $t('manga.navbar.logout') }}</span>
            <i class="fas fa-chevron-right dropdown-arrow"></i>
          </a>
        </div>
      </div>
    </div>
  </nav>

  <!-- 获取算力弹窗 -->
  <GetPowerDialog v-model:visible="showGetPowerDialog" :current-power="userInfo?.power || 0" @purchase="handlePurchase" />

  <!-- 个人信息弹窗 -->
  <UserProfileDialog v-model:visible="showUserProfileDialog" :user-info="userInfo" @save="handleSaveProfile" />

  <!-- 我的订单弹窗 -->
  <PowerOrderDialog v-model:visible="showPowerOrderDialog" />

  <!-- 留言建议弹窗 -->
  <UserMessageDialog v-model:visible="showUserMessageDialog" @success="handleMessageSuccess" />
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { localPath } from '@/i18n'
import { getToken, removeToken } from '@/utils/auth.js'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { getUserInfo } from '@/api/admin/user.js'
import GetPowerDialog from './GetPowerDialog.vue'
import UserProfileDialog from './UserProfileDialog.vue'
import PowerOrderDialog from './PowerOrderDialog.vue'
import UserMessageDialog from './UserMessageDialog.vue'

const { t, locale } = useI18n()
const activeTab = ref('free')
const showLangMenu = ref(false)

const langOptions = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh-TW', label: '繁體中文' }
]

const langLabel = computed(() => {
  const found = langOptions.find(l => l.value === locale.value)
  return found ? found.label : '中文'
})

const selectLang = (lang) => {
  locale.value = lang
  localStorage.setItem('lang', lang)
  const path = router.currentRoute.value.fullPath
  const newPath = path.replace(/^\/(zh-TW|zh|en|ja|ko)/, `/${lang}`)
  router.push(newPath)
  showLangMenu.value = false
}

const showUserMenu = ref(false)
const hideTimeout = ref(null)
const isScrolled = ref(false)
const router = useRouter()
const route = useRoute()
const store = useStore()

// 滚动检测
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

// 用户信息状态
const userInfo = ref({})
const taskCount = ref(0)

// 获取算力弹窗状态
const showGetPowerDialog = ref(false)

// 个人信息弹窗状态
const showUserProfileDialog = ref(false)

// 我的订单弹窗状态
const showPowerOrderDialog = ref(false)

// 留言建议弹窗状态
const showUserMessageDialog = ref(false)

// 计算是否已登录
const isLoggedIn = computed(() => {
  const token = getToken()
  return !!(token && token !== undefined)
})

// 获取用户显示名称
const getUserDisplayName = () => {
  if (userInfo.value && userInfo.value.nickname) {
    return userInfo.value.nickname
  }
  return t('manga.navbar.user')
}

// 获取用户头像 initials
const getUserInitials = () => {
  if (userInfo.value && userInfo.value.nickname) {
    return userInfo.value.nickname.charAt(0).toUpperCase()
  }
  return 'U'
}

// 获取任务数量
const getTaskCount = () => {
  return t('manga.navbar.taskCount', { count: taskCount.value })
}

// 获取当前算力
const getComputingPower = () => {
  return userInfo.value?.mangaUser?.power?.toString() || '0'
}

// 根据当前路由设置active状态
const updateActiveTab = () => {
  const path = route.path.replace(/\/+$/, '')
  if (path === '/vip' || path.endsWith('/vip')) {
    activeTab.value = 'vip'
  } else {
    activeTab.value = 'free'
  }
}

// 获取用户信息
const fetchUserInfo = async () => {

  // 只有当用户已登录时才调用用户信息API
  if (!isLoggedIn.value) {

    // 未登录用户设置默认值
    computingPower.value = 0
    taskCount.value = 0
    return
  }

  try {
    const { data } = await getUserInfo()
    userInfo.value = data
    userInfo.value.power = userInfo.value?.mangaUser?.power || 0


    computingPower.value = userInfo.value;

    taskCount.value = data.taskCount || data.tasks || 0
  } catch (error) {
    console.error('获取用户信息失败:', error)
    // 设置默认值
    computingPower.value = 0
    taskCount.value = 0
  }
}

// 监听刷新用户信息事件
const handleRefreshUserInfo = () => fetchUserInfo()

// 组件挂载时设置active状态并获取用户信息
onMounted(() => {
  updateActiveTab()
  fetchUserInfo()
  window.addEventListener('refresh-user-info', handleRefreshUserInfo)
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('refresh-user-info', handleRefreshUserInfo)
  window.removeEventListener('scroll', handleScroll)
})

// 监听路由变化，自动更新active状态
watch(() => route.path, () => {
  updateActiveTab()
})

const setActiveTab = (tab) => {
  if (tab === 'free') {
    activeTab.value = 'free'
    router.push(localPath('/MangaTranslation'))
  } else if (tab === 'vip') {
    const token = getToken()
    if (!token || token === undefined) {
      router.push(localPath('/login'))
    } else {
      activeTab.value = 'vip'
      router.push(localPath('/vip'))
    }
  }
}

// 处理头像点击
const handleAvatarClick = () => {
  if (!isLoggedIn.value) {
    router.push(localPath('/login'))
  }
}

// 处理头像鼠标进入
const handleAvatarMouseEnter = () => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  showUserMenu.value = true
}

// 处理头像鼠标离开
const handleAvatarMouseLeave = () => {
  // 延迟隐藏，给用户时间移动到下拉菜单
  hideTimeout.value = setTimeout(() => {
    showUserMenu.value = false
  }, 200)
}

// 处理下拉菜单鼠标进入
const handleDropdownMouseEnter = () => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  showUserMenu.value = true
}

// 处理下拉菜单鼠标离开
const handleDropdownMouseLeave = () => {
  hideTimeout.value = setTimeout(() => {
    showUserMenu.value = false
  }, 200)
}

// 处理我的翻译
const handleMyTranslations = () => {
  showUserMenu.value = false
  router.push({ path: localPath('/vip'), query: { scrollTo: 'history' } })
}

// 处理个人中心
const handleProfile = () => {
  showUserMenu.value = false
  // TODO: 跳转到个人中心页面
  console.log('跳转到个人中心')
  // 这里可以添加路由跳转逻辑
  // router.push('/profile')
}

// 处理获取算力
const handleGetPower = () => {
  showUserMenu.value = false
  showGetPowerDialog.value = true
}

// 处理我的订单
const handleMyOrders = () => {
  showUserMenu.value = false
  showPowerOrderDialog.value = true
}

// 处理个人信息
const handleUserProfile = () => {
  showUserMenu.value = false
  showUserProfileDialog.value = true
}

// 处理留言建议
const handleUserMessage = () => {
  showUserMenu.value = false
  showUserMessageDialog.value = true
}

// 处理留言提交成功
const handleMessageSuccess = () => {
  // 可以刷新用户信息等
}

// 处理购买算力
const handlePurchase = (purchaseData) => {
  console.log('购买算力:', purchaseData)
  showGetPowerDialog.value = false
  fetchUserInfo()
}

// 处理保存个人信息
const handleSaveProfile = (profileData) => {
  console.log('保存个人信息:', profileData)
  // 这里可以添加保存逻辑，比如调用API
  // TODO: 调用保存API，成功后更新userInfo
  userInfo.value = { ...userInfo.value, ...profileData }
}

// 处理退出登录
const handleLogout = () => {
  showUserMenu.value = false
  removeToken()
  router.push(localPath('/login'))
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 5%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.navbar.scrolled {
  background: rgba(10, 15, 26, 0.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.home-link {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.logo-img {
  height: 2rem;
  width: auto;
  filter: drop-shadow(0 2px 8px rgba(94, 234, 212, 0.3));
}

.home-link span {
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-menu {
  display: flex;
  gap: 2rem;
}

.user-avatar {
  position: relative;
  cursor: pointer;
}

.avatar-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(94, 234, 212, 0.3);
  transition: all 0.3s ease;
}

.avatar-img:hover {
  border-color: #5eead4;
  transform: scale(1.05);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  min-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

.user-profile-section {
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.user-avatar-large {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  margin-right: 12px;
}

.user-info-dropdown h3 {
  font-size: 16px;
  margin-bottom: 4px;
  color: #fff;
}

.user-name-dropdown {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #fff;
}

.user-login-method {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}

.user-login-method i {
  color: #07c160;
  margin-right: 4px;
}

.dropdown-divider {
  height: 1px;
  background-color: #333;
  margin: 0;
}

.resource-section {
  padding: 15px 20px;
  background-color: #252525;
  display: flex;
  gap: 15px;
}

.resource-item {
  flex: 1;
  display: flex;
  align-items: center;
}

.resource-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 14px;
}

.resource-info {
  flex: 1;
}

.resource-title {
  font-size: 11px;
  color: #aaa;
  margin-bottom: 2px;
}

.resource-value {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}

.dropdown-item-with-icon {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  text-decoration: none;
  color: #fff;
  transition: background-color 0.2s;
  border-bottom: 1px solid #333;
}

.dropdown-item-with-icon:last-child {
  border-bottom: none;
}

.dropdown-item-with-icon:hover {
  background-color: #2f2f2f;
}

.dropdown-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 16px;
}

.dropdown-item-with-icon span {
  flex: 1;
  font-size: 14px;
}

.dropdown-arrow {
  color: #666;
  font-size: 12px;
}

.nav-item,
a.nav-item {
  color: #94a3b8;
  cursor: pointer;
  transition: 0.3s;
  position: relative;
  text-decoration: none;
  font-size: 0.9rem;
}

.nav-item:hover {
  color: #5eead4;
}

.nav-item.active {
  color: #5eead4;
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 2px;
  background: #5eead4;
  border-radius: 1px;
  transform: scaleX(1);
}

.nav-item:not(.active)::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 2px;
  background: #5eead4;
  border-radius: 1px;
  transform: scaleX(0);
  transition: transform 0.3s;
}

.nav-item:not(.active):hover::after {
  transform: scaleX(1);
}

.lang-dropdown {
  position: relative;
}

.lang-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.15);
  color: #e2e8f0;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  user-select: none;
  line-height: 1;
}

.lang-dropdown-trigger .fa-globe {
  font-size: 0.9rem;
  color: #5eead4;
  vertical-align: middle;
  transform: translateY(1px);
}

.lang-dropdown-trigger .fa-chevron-down {
  font-size: 0.55rem;
  color: #5eead4;
  margin-left: 2px;
  transition: transform 0.3s ease;
  vertical-align: middle;
  transform: translateY(1px);
}

.lang-dropdown-trigger .fa-chevron-down.open {
  transform: translateY(1px) rotate(180deg);
}

.lang-dropdown-trigger:hover {
  background: rgba(94, 234, 212, 0.15);
  border-color: rgba(94, 234, 212, 0.3);
  box-shadow: 0 0 12px rgba(94, 234, 212, 0.1);
}

.lang-dropdown-trigger i:first-child {
  color: #5eead4;
  font-size: 0.85rem;
}

.lang-dropdown-trigger .fa-chevron-down {
  font-size: 0.6rem;
  color: #5eead4;
  transition: transform 0.3s ease;
  margin-left: 2px;
}

.lang-dropdown-trigger .fa-chevron-down.open {
  transform: rotate(180deg);
}

.lang-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 140px;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 10px;
  padding: 6px;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(94, 234, 212, 0.05);
  z-index: 100;
}

.lang-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 6px;
  color: #94a3b8;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-dropdown-item:hover {
  background: rgba(94, 234, 212, 0.1);
  color: #e2e8f0;
}

.lang-dropdown-item.active {
  color: #5eead4;
  background: rgba(94, 234, 212, 0.08);
}

.lang-dropdown-item .fa-check {
  font-size: 0.7rem;
  color: #5eead4;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 900px) {
  .nav-menu {
    display: none;
  }

  .nav-right {
    gap: 1rem;
  }
}

@media (max-width: 600px) {
  .logo {
    display: none;
  }

  .navbar {
    justify-content: space-between;
  }

  .nav-right {
    flex: 1;
    justify-content: space-between;
  }

  .nav-menu {
    display: flex;
  }
}
</style>